//@flow
import React, {useState} from 'react';
import {
  makeStyles,
  FormControlLabel,
  Radio,
  RadioGroup,
  Grid,
  Typography,
} from '@material-ui/core';
import ColorInput from '../../../../../elements/ColorInput';
import {colors} from '../../../../../assets/styles/colors';
import CropModal from '../../../../../elements/CropModal';
import {CloudUpload} from '@material-ui/icons';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {useCustomStoreBackgroundActions} from '../../utils/store-background';
import SnackBar from '../../../../../elements/SnackBar/SnackBar';
import InlineLoader from '../../../../../elements/LoadingSpinner/InlineLoader';
import {getFileUrl} from '../../../../../utils/image_provider';

const useStyles = makeStyles(theme => ({
  sectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    width: '100%',
    alignItems: 'end',
    position: 'relative',
  },
  section: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',

    [theme.breakpoints.up('sm')]: {
      padding: '0 1rem',
      borderRight: `1px solid ${colors.grey300}`,
      '&:last-child': {
        borderRight: 'none',
      },
    },
  },
  sectionContent: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.25rem',
  },
  title: {
    color: colors.grey900,
    fontSize: '14px',
    fontWeight: 600,
  },
  subTitle: {
    color: colors.grey700,
    fontSize: '12px',
    fontWeight: 500,
  },
  patternSection: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    color: colors.primary,
    border: `2px dashed ${colors.grey500}`,
    borderRadius: '1rem',
    aspectRatio: '1/1',
    maxWidth: '400px',
    marginInline: 'auto',
    cursor: 'pointer',
  },
  photoSection: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '1rem',
  },
  desktopPhotoSection: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    color: colors.primary,
    border: `2px dashed ${colors.grey500}`,
    borderRadius: '1rem',
    aspectRatio: '16/9',
    marginInline: 'auto',
    cursor: 'pointer',
    overflow: 'hidden',
  },
  mobilePhotoSection: {
    overflow: 'hidden',
    width: '100%',
    maxWidth: '200px',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    color: colors.primary,
    border: `2px dashed ${colors.grey500}`,
    borderRadius: '1rem',
    aspectRatio: '9/16',
    cursor: 'pointer',
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: 'rgba(255,255,255,0.5)',
    zIndex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

type BackgroundSectionProps = {
  defaultBackgroundType?: 'COLOR' | 'PATTERN' | 'PHOTO',
  defaultBackgroundColor?: string,
  defaultPatternImage?: string,
  defaultDesktopPhotoImage?: string,
  defaultMobilePhotoImage?: string,
  defaultBackgroundEnabled?: boolean,
};

export default function BackgroundSection(props: BackgroundSectionProps) {
  const {
    defaultBackgroundType,
    defaultBackgroundColor = '#FFFFFF',
    defaultPatternImage,
    defaultDesktopPhotoImage,
    defaultMobilePhotoImage,
    defaultBackgroundEnabled,
  } = props;
  const classes = useStyles();
  const [backgroundType, setBackgroundType] = useState(() => {
    if (defaultBackgroundEnabled) return defaultBackgroundType;
    return 'NONE';
  });
  const [backgroundColor, setBackgroundColor] = useState(
    defaultBackgroundColor ?? '#FFFFFF'
  );

  const [saveSuccess, setSaveSuccess] = useState(false);

  const {
    error,
    isLoading,
    updateStoreBackground,
  } = useCustomStoreBackgroundActions();

  const [patternImageToCrop, setPatternImageToCrop] = useState(undefined);
  const [patternImage, setPatternImage] = useState(undefined);
  const [patternImagePreview, setPatternImagePreview] = useState(() => {
    if (defaultPatternImage) {
      return getFileUrl(defaultPatternImage);
    }
    return undefined;
  });

  console.log({patternImageToCrop});

  const handleUploadPatternImage = e => {
    const fileList = Array.from(e.target.files);
    if (fileList.length > 0) {
      setPatternImageToCrop(fileList);
    }
  };
  const [desktopPhotoImageToCrop, setDesktopPhotoImageToCrop] = useState(
    undefined
  );
  const [desktopPhotoImage, setDesktopPhotoImage] = useState(undefined);
  const [desktopPhotoImagePreview, setDesktopPhotoImagePreview] = useState(
    () => {
      if (defaultDesktopPhotoImage) {
        return getFileUrl(defaultDesktopPhotoImage);
      }

      return undefined;
    }
  );

  const handleUploadDesktopPhotoImage = e => {
    const fileList = Array.from(e.target.files);
    if (fileList.length > 0) {
      setDesktopPhotoImageToCrop(fileList);
    }
  };

  const [mobilePhotoImageToCrop, setMobilePhotoImageToCrop] = useState(
    undefined
  );
  const [mobilePhotoImage, setMobilePhotoImage] = useState(undefined);
  const [mobilePhotoImagePreview, setMobilePhotoImagePreview] = useState(() => {
    if (defaultMobilePhotoImage) {
      return getFileUrl(defaultMobilePhotoImage);
    }
    return undefined;
  });

  const handleUploadMobilePhotoImage = e => {
    const fileList = Array.from(e.target.files);
    if (fileList.length > 0) {
      setMobilePhotoImageToCrop(fileList);
    }
  };

  const handleSaveBackground = () => {
    if (backgroundType === 'NONE') {
      return updateStoreBackground({
        backgroundEnabled: false,
      }).then(() => {
        setSaveSuccess(true);
      });
    }
    updateStoreBackground({
      backgroundType,
      backgroundColor,
      backgroundEnabled: true,
      backgroundPhotoDesktopFile: desktopPhotoImage,
      backgroundPhotoMobileFile: mobilePhotoImage,
      backgroundPatternFile: patternImage,
      backgroundPatternFileId: patternImage ? undefined : defaultPatternImage,
      backgroundPhotoDesktopFileId: desktopPhotoImage
        ? undefined
        : defaultDesktopPhotoImage,
      backgroundPhotoMobileFileId: mobilePhotoImage
        ? undefined
        : defaultMobilePhotoImage,
    }).then(() => {
      setSaveSuccess(true);
    });
  };

  return (
    <div className={classes.sectionContainer}>
      <SnackBar mode="error" message={error} />
      <SnackBar
        mode="success"
        message={saveSuccess ? 'Guardado con éxito' : ''}
        handleClose={() => setSaveSuccess(false)}
      />
      {isLoading && (
        <div className={classes.loader}>
          <InlineLoader loading size={40} />
        </div>
      )}
      <Grid container>
        <Grid item xs={12} sm={3} md={2}>
          <RadioGroup
            aria-label="Tipo de fondo"
            onChange={e => setBackgroundType(e.target.value)}
            value={backgroundType}
          >
            <FormControlLabel
              value="NONE"
              control={<Radio color="primary" />}
              label="Sin fondo"
            />
            <FormControlLabel
              value="COLOR"
              control={<Radio color="primary" />}
              label="Color"
            />
            <FormControlLabel
              value="PATTERN"
              control={<Radio color="primary" size="medium" />}
              label="Trama"
            />
            <FormControlLabel
              value="PHOTO"
              control={<Radio color="primary" />}
              label="Imagen"
            />
          </RadioGroup>
        </Grid>

        <Grid item xs={12} sm={9} md={10} alignItems="center" justify="center">
          {backgroundType === 'COLOR' && (
            <section className={classes.section}>
              <header className={classes.sectionHeader}>
                <Typography className={classes.title}>
                  Color de fondo
                </Typography>
                <Typography className={classes.subTitle}>Código Hex</Typography>
              </header>

              <ColorInput
                value={backgroundColor}
                onChange={value => setBackgroundColor(value)}
              />
            </section>
          )}
          {backgroundType === 'PATTERN' && (
            <label
              className={classes.patternSection}
              style={
                patternImagePreview && {
                  backgroundImage: `url(${patternImagePreview})`,
                  backgroundSize: '25%',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'repeat',
                }
              }
            >
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleUploadPatternImage}
              />

              {!patternImagePreview && (
                <>
                  <CloudUpload />
                  <Typography align="center">
                    Sube una imagen o gráfico <br /> para armar una trama
                  </Typography>
                </>
              )}

              {patternImageToCrop && (
                <CropModal
                  restrictPosition
                  open={!!patternImageToCrop}
                  dynamicAspectRatio={{
                    rectangle: {
                      aspect: 16 / 9,
                    },
                    square: {
                      aspect: 1,
                    },
                  }}
                  compressOptions={{
                    maxHeight: 600,
                    maxWidth: 600,
                    quality: 0.85,
                    customFormat: 'jpeg',
                  }}
                  onClose={() => setPatternImageToCrop(undefined)}
                  onSubmit={results => {
                    const result = results[0];
                    if (!result) return;
                    setPatternImage(result.file);
                    setPatternImagePreview(result.preview);

                    setPatternImageToCrop(undefined);
                  }}
                  images={patternImageToCrop.map(f => ({
                    preview: URL.createObjectURL(f),
                    file: f,
                  }))}
                />
              )}
            </label>
          )}
          {backgroundType === 'PHOTO' && (
            <div className={classes.photoSection}>
              <label className={classes.desktopPhotoSection}>
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleUploadDesktopPhotoImage}
                />
                {desktopPhotoImagePreview && (
                  <img
                    src={desktopPhotoImagePreview}
                    alt="Desktop"
                    style={{width: '100%', height: '100%'}}
                  />
                )}
                {!desktopPhotoImagePreview && (
                  <>
                    <CloudUpload />
                    <Typography align="center">
                      Sube una imagen para PC <br />
                      (1920 × 1080 px)
                    </Typography>
                  </>
                )}
              </label>

              <label className={classes.mobilePhotoSection}>
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleUploadMobilePhotoImage}
                />

                {mobilePhotoImagePreview && (
                  <img
                    src={mobilePhotoImagePreview}
                    alt="Mobile"
                    style={{width: '100%', height: '100%'}}
                  />
                )}
                {!mobilePhotoImagePreview && (
                  <>
                    <CloudUpload />
                    <Typography align="center">
                      Sube una imagen para celular <br />
                      (720 x 1280 px)
                    </Typography>
                  </>
                )}
              </label>

              {desktopPhotoImageToCrop && (
                <CropModal
                  restrictPosition
                  open={!!desktopPhotoImageToCrop}
                  aspect={16 / 9}
                  onClose={() => setDesktopPhotoImageToCrop(undefined)}
                  compressOptions={{
                    maxHeight: 1080,
                    maxWidth: 1920,
                    quality: 1,
                  }}
                  onSubmit={results => {
                    const result = results[0];
                    if (!result) return;
                    setDesktopPhotoImage(result.file);
                    setDesktopPhotoImagePreview(result.preview);

                    setDesktopPhotoImageToCrop(undefined);
                  }}
                  images={desktopPhotoImageToCrop.map(f => ({
                    preview: URL.createObjectURL(f),
                    file: f,
                  }))}
                />
              )}

              {mobilePhotoImageToCrop && (
                <CropModal
                  restrictPosition
                  open={!!mobilePhotoImageToCrop}
                  aspect={9 / 16}
                  onClose={() => setMobilePhotoImageToCrop(undefined)}
                  compressOptions={{
                    maxWidth: 720,
                    maxHeight: 1280,
                    quality: 0.85,
                  }}
                  onSubmit={results => {
                    const result = results[0];
                    if (!result) return;
                    setMobilePhotoImage(result.file);
                    setMobilePhotoImagePreview(result.preview);

                    setMobilePhotoImageToCrop(undefined);
                  }}
                  images={mobilePhotoImageToCrop.map(f => ({
                    preview: URL.createObjectURL(f),
                    file: f,
                  }))}
                />
              )}
            </div>
          )}
        </Grid>
      </Grid>

      <CustomButton
        variant="contained"
        color="primary"
        type="button"
        onClick={handleSaveBackground}
        disabled={isLoading}
      >
        Guardar Fondo
      </CustomButton>
    </div>
  );
}
